<template>
  <div class="min-h-screen bg-gray-100 flex flex-row w-full">
    <div class="hidden md:flex h-screen items-center justify-center" style="width:70%; min-width:300px; background:darkgray;">
      <div class="relative w-full h-full flex items-center justify-center">
        <video 
          class="w-full h-full object-contain"
          autoplay 
          loop 
          muted
          playsinline
        >
          <source src="@/assets/intro/SecureAI_이니텍_v1.mp4" type="video/mp4">
        </video>
      </div>
    </div>
    <!-- 오른쪽 로그인 폼 -->
    <div class="flex h-screen items-center justify-center w-full" id="login-right">
      <div class="w-full max-w-md mx-auto">
        <form class="space-y-6" @submit.prevent="handleLogin">
          <div>
            <img src="@/assets/icons/logo.png" alt="Secure AI Logo" class="h-11 mb-14 mx-auto object-contain" title="보안 AI 로고" />
            <label for="email" class="block text-sm font-medium text-gray-700" title="이메일 주소">
              이메일 주소
            </label>
            <div class="mt-1">
              <input
                id="email"
                v-model.trim="email"
                type="text"
                required
                placeholder="등록된 이메일 주소를 입력하세요"
                class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                @input="validateEmail"
              />
              <p v-if="emailError" class="mt-1 text-sm text-red-600">{{ emailError }}</p>
            </div>
          </div>

          <div>
            <label for="password" class="block text-sm font-medium text-gray-700" title="비밀번호">
              비밀번호
            </label>
            <div class="mt-1">
              <input
                id="password"
                v-model.trim="password"
                type="password"
                required
                placeholder="지정된 비밀번호를 입력하세요"
                class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                @input="validatePassword"
              />
              <p v-if="passwordError" class="mt-1 text-sm text-red-600">{{ passwordError }}</p>
            </div>
          </div>

          <!-- 에러 메시지 표시 영역 -->
          <div v-if="error" class="text-sm text-red-600">
            {{ error }}
          </div>

          <div>
            <button
              type="submit"
              :disabled="!isFormValid"
              :class="[
                'w-full font-bold py-2 px-4 rounded-lg',
                isFormValid ? 'bg-primary hover:bg-primary/90 text-white' : 'bg-gray-300 cursor-not-allowed text-gray-500',
                'no-focus-style'
              ]"
              title="로그인"
            >
              로그인
            </button>
          </div>

          <div class="relative">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-gray-300"></div>
            </div>
            <div class="relative flex justify-center text-sm">
              <span class="px-2 bg-gray-100 text-gray-500">또는</span>
            </div>
          </div>

          <div>
            <button
              type="button"
              @click="handleIntegratedAuth"
              :disabled="!ihubReady || ihubError"
              :class="[
                'w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium no-focus-style',
                ihubReady && !ihubError ? 'text-gray-700 bg-white hover:bg-gray-50' : 'bg-gray-300 cursor-not-allowed text-gray-500'
              ]"
              title="통합인증으로 로그인"
            >
              통합인증으로 로그인
            </button>
            <div v-if="integratedAuthError" class="mt-2 text-red-600 text-sm">{{ integratedAuthError }}</div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { handleApiError } from '@/utils/apiErrorHandler';
import bcrypt from 'bcryptjs';
import CryptoJS from 'crypto-js';

const LOGIN_ERROR_MSG = '이메일이 없거나 비밀번호가 틀렸습니다.';

// AES 복호화 함수
function decryptAES(encryptedData, key, iv) {
  if (!encryptedData || encryptedData.trim() === '') {
    return '';
  }

  try {
    const keyWordArray = CryptoJS.enc.Base64.parse(key);
    const ivWordArray = CryptoJS.enc.Base64.parse(iv);

    const decrypted = CryptoJS.AES.decrypt(encryptedData, keyWordArray, {
      iv: ivWordArray,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });

    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    console.error('[AES] 복호화 실패:', error);
    return '';
  }
}

export default {
  name: 'LoginPage',
  data() {
    return {
      email: '',
      password: '',
      error: '',
      integratedAuthError: '',
      emailError: '',
      passwordError: '',
      AUTHORIZED_EMAIL: 'idjss12@chloe-soft.com',
      ADMIN_EMAIL: 'admin@chloe-soft.com',
      TEMP_PASSWORD: '1234'
    }
  },
  computed: {
    ihubReady() {
      return this.$store.state.ihubReady;
    },
    ihubError() {
      return this.$store.state.ihubError;
    },
    isFormValid() {
      return this.email &&
             this.password &&
             !this.emailError &&
             !this.passwordError &&
             this.password.length >= 4
    }
  },
  methods: {
    validateEmail() {
      this.emailError = ''
      this.error = ''
      
      if (!this.email) {
        this.emailError = '이메일을 입력해주세요.'
        return
      }

      if (this.email === this.ADMIN_EMAIL) {
        return
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(this.email)) {
        this.emailError = '올바른 이메일 형식이 아닙니다.'
      }
    },
    validatePassword() {
      this.passwordError = ''
      this.error = ''
      
      if (!this.password) {
        this.passwordError = '비밀번호를 입력해주세요.'
        return
      }

      if (this.password.length < 4) {
        this.passwordError = '비밀번호는 최소 4자 이상이어야 합니다.'
      }
    },
    async handleLogin() {
      try {
        this.error = ''
        // 이메일 유효성 검사
        const normalizedEmail = this.email.toLowerCase().trim()
        // admin 계정은 이메일 형식 검증 건너뜀
        if (normalizedEmail !== 'admin') {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          if (!emailRegex.test(normalizedEmail)) {
            this.error = '올바른 이메일 형식이 아닙니다.'
            return
          }
        }
        // bcrypt.compare를 사용하여 비밀번호 비교
        const isPasswordValid = await bcrypt.compare(this.password, '$2b$10$5lIuI1cQDFX9tTXhnPNAuORpA39AYD8BcSm/P0hZEGfVkwyHUl.sW');
        if (!isPasswordValid) {
          this.error = LOGIN_ERROR_MSG;
          return
        }

        // admin@chloe-soft.com 계정 하드코딩 로그인 (테스트용)
        // if (normalizedEmail === 'admin@chloe-soft.com' && this.password === '1234') {
        //   // Postman에서 받은 JWT 사용
        //   const jwt = 'eyJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJzZ3RfZ2F0ZXdheSIsInN1YiI6ImFkbWluQGNobG9lLXNvZnQuY29tIiwiaWF0IjoxNzQ5NTMwNDc5LCJleHAiOjIwNjQ4OTA0NzksInNjb3BlIjpbIlVTRVJfSU5GTyJdLCJ0aWVyX2luZm8iOiJiYXNpYyJ9.5oicIocxKoJ4SQEmR9JlGRn7DNFB3fETN_pzoizmrpk';
        //   localStorage.setItem('token', jwt);
        //   localStorage.setItem('userInfo', JSON.stringify({ email: 'admin@chloe-soft.com' }));
        //   this.$store.commit('SET_TOKEN', jwt);
        //   this.$router.push('/chat');
        //   return;
        // }
        // JWT 발급 요청 (admin 포함 모든 계정)
        const { INITIAL_JWT, API_ENDPOINTS } = await import('@/utils/constants');
        const res = await fetch(`${process.env.VUE_APP_SECURE_AI_CLIENT_SDK_URL}${API_ENDPOINTS.USER_CREDENTIALS}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${INITIAL_JWT}`
          },
          body: JSON.stringify({ userId: normalizedEmail })
        })
        const data = await res.json()
        if (!data.success || !data.data?.jwt) {
          this.error = LOGIN_ERROR_MSG;
          return
        }
        // JWT와 userId(email) 저장 (여러 계정 지원)
        localStorage.setItem('token', data.data.jwt)
        localStorage.setItem('userInfo', JSON.stringify({
          userId: normalizedEmail,
          email: normalizedEmail
        }))
        this.$store.commit('SET_TOKEN', data.data.jwt)

        // 🚀 JWT 관리자 초기화 (역할별 JWT 발급을 위한 기본 JWT 설정)
        try {
          const { initializeJWTManager } = await import('@/utils/api');
          initializeJWTManager(data.data.jwt);
          console.log('[Login] JWT 관리자 초기화 완료');
        } catch (e) {
          console.error('[Login] JWT 관리자 초기화 중 오류:', e);
        }

        // 🚀 기본 SDK 최초 초기화
        try {
          const { initializeDefaultSDK } = await import('@/utils/secureSDK');
          await initializeDefaultSDK(data.data.jwt);
          console.log('[Login] 기본 SDK 초기화 완료');
        } catch (e) {
          console.error('[Login] SDK 초기화 중 오류:', e);
          // SDK 초기화 실패해도 로그인은 진행 (나중에 Chat에서 fallback 처리)
        }
        
        this.$router.push('/chat')
      } catch (error) {
        handleApiError(error, {
          showModal: (msg) => {
            this.error = msg;
          }
        });
      }
    },
    loginSuccess(name, email) {
      // 더 이상 하드코딩된 토큰 저장하지 않음
      const userInfo = { name, email }
      localStorage.setItem('userInfo', JSON.stringify(userInfo))
      this.$router.push('/chat')
    },
    async handleIntegratedAuth() {
      try {
        // 에러 초기화
        this.integratedAuthError = '';

        // IHUB_C 전역 객체 및 초기화 상태 확인
        if (typeof window.IHUB_C === 'undefined') {
          this.integratedAuthError = '통합인증 SDK가 로드되지 않았습니다. 페이지를 새로고침해주세요.'
          console.error('[INIHUB] IHUB_C 객체가 정의되지 않음');
          return
        }

        if (!this.ihubReady) {
          this.integratedAuthError = '통합인증 초기화 중입니다. 잠시 후 다시 시도해주세요.'
          console.warn('[INIHUB] 초기화가 아직 완료되지 않음');
          return
        }

        if (this.ihubError) {
          this.integratedAuthError = '통합인증 초기화에 실패했습니다: ' + (this.ihubError.message || JSON.stringify(this.ihubError))
          console.error('[INIHUB] 초기화 에러:', this.ihubError);
          return
        }

        console.log('[INIHUB] 통합인증 로그인 시작')

        // 통합인증창 호출
        let result;
        try {
          result = await window.IHUB_C.login({
            authParam: {}
          })
        } catch (loginError) {
          // resCode 1300: 사용자 취소 (창 닫기) - 에러 메시지 표시 안 함
          if (loginError?.resCode === 1300 || loginError?.message?.includes('취소') || loginError?.message?.includes('cancel')) {
            console.log('[INIHUB] 사용자가 인증을 취소했습니다.')
            return
          }
          // 실제 에러인 경우에만 메시지 표시
          console.error('[INIHUB] 통합인증 로그인 실패:', loginError)
          this.integratedAuthError = '통합인증 로그인 중 오류가 발생했습니다.'
          return
        }

        console.log('[INIHUB] 인증 성공, authToken:', result.authToken)

        // 1단계: 접근토큰 발급 요청
        const baseUrl = process.env.VUE_APP_INIHUB_BASE_URL;
        const clientId = process.env.VUE_APP_INIHUB_CLIENT_ID;
        const clientSecret = process.env.VUE_APP_INIHUB_CLIENT_SECRET;

        if (!clientSecret) {
          this.integratedAuthError = 'INIHub CLIENT_SECRET이 설정되지 않았습니다.';
          console.error('[INIHUB] CLIENT_SECRET 미설정');
          return;
        }

        console.log('[INIHUB] 접근토큰 발급 요청');

        const credentials = btoa(`${clientId}:${clientSecret}`);
        const tokenRes = await fetch(`${baseUrl}/auth/oauth/v2/token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            'Authorization': `Basic ${credentials}`
          },
          body: 'grant_type=client_credentials'
        });

        if (!tokenRes.ok) {
          const errorText = await tokenRes.text();
          console.error('[INIHUB] 접근토큰 발급 실패:', errorText);
          this.integratedAuthError = `접근토큰 발급 실패 (${tokenRes.status})`;
          return;
        }

        const tokenData = await tokenRes.json();
        const accessToken = tokenData.access_token;
        console.log('[INIHUB] 접근토큰 발급 성공:', accessToken);

        // 2단계: 인증토큰 검증 요청
        console.log('[INIHUB] 인증토큰 검증 요청');

        const verifyRes = await fetch(`${baseUrl}/api/v1/ezauth/authtoken/verify.do`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json; charset=UTF-8',
            'Authorization': `Bearer ${accessToken}`
          },
          body: JSON.stringify({
            authToken: result.authToken
          })
        });

        if (!verifyRes.ok) {
          const errorText = await verifyRes.text();
          console.error('[INIHUB] 인증토큰 검증 실패:', errorText);
          this.integratedAuthError = `인증토큰 검증 실패 (${verifyRes.status})`;
          return;
        }

        const verifyData = await verifyRes.json();
        console.log('[INIHUB] 인증토큰 검증 응답:', verifyData);

        if (verifyData.resCode !== 1200) {
          console.error('[INIHUB] 검증 실패:', verifyData.errorMessage);
          this.integratedAuthError = `인증 실패: ${verifyData.errorMessage}`;
          return;
        }

        // 3단계: payload 복호화 및 userId 생성
        console.log('[INIHUB] payload 복호화');

        const payload = verifyData.payload;
        const aesKey = process.env.VUE_APP_INIHUB_AES_KEY;
        const aesIv = process.env.VUE_APP_INIHUB_AES_IV;

        if (!aesKey || !aesIv) {
          this.integratedAuthError = 'AES KEY/IV가 설정되지 않았습니다.';
          console.error('[INIHUB] AES KEY/IV 미설정');
          return;
        }


        // 복호화
        const userName = decryptAES(payload.uname, aesKey, aesIv);
        const userBirthday = decryptAES(payload.ubirthday, aesKey, aesIv);
        const userPhone = decryptAES(payload.uphone, aesKey, aesIv);
        const userSex = decryptAES(payload.usex, aesKey, aesIv);
        const userForeigner = decryptAES(payload.uforeigner, aesKey, aesIv);
        const userCI = decryptAES(payload.uci, aesKey, aesIv);


        // userId 생성 (SHA256 해시의 앞 32자리)
        const combinedString = `${userName}_${userPhone}`;
        const userId = CryptoJS.SHA256(combinedString).toString().substring(0, 32);
        console.log('[INIHUB] 생성된 userId :', userId);

        // 백엔드로 userId 전송하여 JWT 발급
        console.log('[SECUREAI] JWT 발급 요청');

        const { INITIAL_JWT, API_ENDPOINTS } = await import('@/utils/constants')
        const jwtRes = await fetch(`${process.env.VUE_APP_SECURE_AI_CLIENT_SDK_URL}${API_ENDPOINTS.USER_CREDENTIALS}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${INITIAL_JWT}`
          },
          body: JSON.stringify({
            userId: userId
          })
        });

        const jwtData = await jwtRes.json();
        if (!jwtData.success || !jwtData.data?.jwt) {
          this.integratedAuthError = '연동된 통합인증 계정이 없습니다. 관리자에게 문의해주세요.';
          return;
        }

        // JWT 저장 및 로그인 처리
        const userInfo = {
          userId: userId,
          email: userId,
          name: userName,
          birthday: userBirthday,
          phone: userPhone,
          sex: userSex,
          foreigner: userForeigner,
          ci: userCI,
          authToken: result.authToken
        };

        localStorage.setItem('token', jwtData.data.jwt)
        localStorage.setItem('userInfo', JSON.stringify(userInfo))
        this.$store.commit('SET_TOKEN', jwtData.data.jwt)

        // JWT 관리자 및 SDK 초기화
        try {
          const { initializeJWTManager } = await import('@/utils/api')
          initializeJWTManager(jwtData.data.jwt)
          console.log('[Login] JWT 관리자 초기화 완료')
        } catch (e) {
          console.error('[Login] JWT 관리자 초기화 중 오류:', e)
        }

        try {
          const { initializeDefaultSDK } = await import('@/utils/secureSDK')
          await initializeDefaultSDK(jwtData.data.jwt)
          console.log('[Login] 기본 SDK 초기화 완료')
        } catch (e) {
          console.error('[Login] SDK 초기화 중 오류:', e)
        }

        this.$router.push('/chat')
      } catch (error) {
        console.error('[INIHUB] 예상치 못한 오류:', error)
        this.integratedAuthError = `통합인증 처리 중 예상치 못한 오류가 발생했습니다: ${error.message || '알 수 없는 오류'}`
      }
    }
  }
}
</script>

<style scoped>
.bg-gray-100 {
  background-color: #f3f4f6;
}

.bg-gray-200 {
  background-color: #e5e7eb;
}

/* 버튼 포커스 스타일 제거 */
.no-focus-style:focus {
  outline: none !important;
  box-shadow: none !important;
}

/* 데스크탑(768px 이상)에서만 오른쪽 영역 30%로 고정 */
#login-right {
  width: 100%;
  padding-left: 24px;
  padding-right: 24px;
  box-sizing: border-box;
}
@media (min-width: 768px) {
  #login-right {
    width: 30% !important;
    padding-left: 32px;
    padding-right: 32px;
  }
}
</style> 